import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, Download, FileText, AlertCircle, X } from 'lucide-react';
import { toast } from 'react-hot-toast';

// Generic CSV import modal. Pass an uploadFn(file) that resolves to
// { success, summary: { successCount, errorCount, errors } } and a templateFn().
const CSVImportModal = ({ title, entityLabel = 'records', uploadFn, templateFn, onUploadComplete, onClose, note }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const onDrop = useCallback((accepted) => {
    const csvFiles = accepted.filter(f => f.type === 'text/csv' || f.name.endsWith('.csv'));
    if (csvFiles.length === 0) { toast.error('Please select a valid CSV file'); return; }
    setFile(csvFiles[0]); setResult(null);
  }, []);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, accept: { 'text/csv': ['.csv'], 'application/vnd.ms-excel': ['.csv'] }, multiple: false });

  const handleTemplate = async () => {
    try { await templateFn(); toast.success('Template downloaded'); }
    catch (e) { toast.error('Failed to download template'); }
  };

  const handleUpload = async () => {
    if (!file) { toast.error('Select a CSV file'); return; }
    setUploading(true); setResult(null);
    try {
      const res = await uploadFn(file);
      setResult(res);
      const s = res.summary || {};
      if ((s.errorCount || 0) === 0) {
        toast.success(`Imported ${s.successCount || 0} ${entityLabel}`);
        if (onUploadComplete) onUploadComplete(res);
      } else {
        toast.error(`Imported ${s.successCount || 0}, ${s.errorCount} error(s) — see below`);
      }
    } catch (e) {
      toast.error('Upload failed: ' + (e.response?.data?.error || e.message || 'Unknown error'));
    } finally { setUploading(false); }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 max-w-2xl w-full mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600" title="Close"><X size={24} /></button>
      </div>

      <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-blue-900 mb-1">Download CSV Template</h3>
          <p className="text-blue-700 text-sm">Use the template so your file has the correct columns.</p>
        </div>
        <button onClick={handleTemplate} className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700" title="Download template">
          <Download size={20} className="mr-2" /> Download Template
        </button>
      </div>

      <div {...getRootProps()} className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors mb-6 ${isDragActive ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}`}>
        <input {...getInputProps()} />
        <Upload size={48} className="mx-auto text-gray-400 mb-4" />
        {isDragActive ? <p className="text-blue-600 font-medium">Drop the CSV file here…</p>
          : <div><p className="text-gray-600 mb-2">Drag and drop a CSV file here, or click to select</p><p className="text-sm text-gray-500">CSV only (max 10MB)</p></div>}
      </div>

      {file && (
        <div className="mb-6 flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
          <div className="flex items-center"><FileText size={20} className="text-gray-500 mr-3" /><div><p className="font-medium text-gray-900">{file.name}</p><p className="text-sm text-gray-500">{(file.size / 1024).toFixed(1)} KB</p></div></div>
          <button onClick={() => { setFile(null); setResult(null); }} className="text-red-500 hover:text-red-700" title="Remove"><X size={20} /></button>
        </div>
      )}

      {result && (
        <div className="mb-6 bg-gray-50 rounded-lg p-4">
          <div className="grid grid-cols-2 gap-4 mb-2">
            <div className="text-center"><div className="text-2xl font-bold text-green-600">{result.summary?.successCount ?? 0}</div><div className="text-sm text-gray-600">Imported</div></div>
            <div className="text-center"><div className="text-2xl font-bold text-red-600">{result.summary?.errorCount ?? 0}</div><div className="text-sm text-gray-600">Errors</div></div>
          </div>
          {result.summary?.errors?.length > 0 && (
            <div className="mt-2 max-h-32 overflow-y-auto">
              {result.summary.errors.map((e, i) => <div key={i} className="text-sm text-red-600">{e}</div>)}
            </div>
          )}
        </div>
      )}

      {note && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded flex items-start">
          <AlertCircle size={20} className="text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-yellow-800">{note}</div>
        </div>
      )}

      <div className="flex justify-end space-x-3">
        <button onClick={() => { setFile(null); setResult(null); }} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Reset</button>
        <button onClick={handleUpload} disabled={!file || uploading} className={`px-6 py-2 rounded-lg ${!file || uploading ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>{uploading ? 'Importing…' : 'Import CSV'}</button>
      </div>
    </div>
  );
};

export default CSVImportModal;
